/**
 * Shopper Approved — shared API client + UI renderers
 * Aggregates + reviews with in-memory dedupe and localStorage SWR (1h).
 */
(function () {
  'use strict';

  if (window.__saReviewsModuleLoaded) return;
  window.__saReviewsModuleLoaded = true;

  var API_BASE = 'https://api.monitors.com/api/v1/shopper-approved';
  var AGGREGATES_URL = API_BASE + '/aggregates';
  var SA_REVIEWS_URL = 'https://www.shopperapproved.com/reviews/monitors.com';
  var STAR_COLOR = '#EB7100';
  var CACHE_TTL_MS = 60 * 60 * 1000;
  var FETCH_TIMEOUT_MS = 15000;
  var CACHE_AGG_KEY = 'sa_agg_v1';
  // Bump when the cached payload shape changes so stale caches don't crash it.
  var CACHE_REV_KEY = 'sa_rev_5_newest_paged_v2';
  var REVIEWS_PAGE_SIZE = 10;
  var LOAD_ROOT_MARGIN = '200px 0px';

  var aggPromise = null;
  var revPromise = null;
  var reviewPagePromises = {};
  var i18n = {};

  function readI18n() {
    var el =
      document.querySelector('.sa-reviews-i18n-data') ||
      document.getElementById('sa-reviews-i18n');
    if (!el) return;
    try {
      i18n = JSON.parse(el.textContent || '{}') || {};
    } catch (e) {
      i18n = {};
    }
  }

  function t(key, fallback) {
    return i18n[key] != null && i18n[key] !== '' ? i18n[key] : fallback;
  }

  function readCache(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ ts: Date.now(), data: data })
      );
    } catch (e) {
      /* quota / private mode */
    }
  }

  function isFresh(entry) {
    return entry && entry.ts && Date.now() - entry.ts < CACHE_TTL_MS && entry.data;
  }

  function fetchJson(url) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeoutId = null;
    var opts = { credentials: 'omit' };
    if (controller) opts.signal = controller.signal;

    var fetchPromise = fetch(url, opts).then(function (res) {
      if (!res.ok) throw new Error('SA fetch failed: ' + res.status);
      return res.json();
    });

    var timeoutPromise = new Promise(function (_, reject) {
      timeoutId = setTimeout(function () {
        if (controller) {
          try {
            controller.abort();
          } catch (e) {}
        }
        reject(new Error('SA fetch timeout'));
      }, FETCH_TIMEOUT_MS);
    });

    return Promise.race([fetchPromise, timeoutPromise]).then(
      function (json) {
        clearTimeout(timeoutId);
        return json;
      },
      function (err) {
        clearTimeout(timeoutId);
        throw err;
      }
    );
  }

  /**
   * Stale-while-revalidate: resolve cached data immediately when present,
   * always refresh in background via shared promise.
   */
  function loadWithSWR(cacheKey, url, getSharedPromise, setSharedPromise) {
    var cached = readCache(cacheKey);
    var network = getSharedPromise();
    if (!network) {
      network = fetchJson(url)
        .then(function (json) {
          if (!json || !json.success || !json.data) {
            throw new Error('SA invalid response');
          }
          writeCache(cacheKey, json.data);
          return json.data;
        })
        .catch(function (err) {
          setSharedPromise(null);
          if (cached && cached.data) return cached.data;
          throw err;
        });
      setSharedPromise(network);
    }

    if (cached && cached.data) {
      // Kick refresh; callers get cache now
      network.catch(function () {});
      return Promise.resolve({ data: cached.data, fromCache: true, fresh: isFresh(cached), refresh: network });
    }

    return network.then(function (data) {
      return { data: data, fromCache: false, fresh: true, refresh: Promise.resolve(data) };
    });
  }

  function getAggregates() {
    return loadWithSWR(
      CACHE_AGG_KEY,
      AGGREGATES_URL,
      function () {
        return aggPromise;
      },
      function (p) {
        aggPromise = p;
      }
    );
  }

  // Without `from`, the endpoint silently defaults to the last 30 days.
  var REVIEWS_FROM_DATE = '2010-01-01';

  // `total_count` in the response is just this page's item count, not a
  // grand total — "more pages?" is inferred from a full page coming back.
  function fetchReviewsPageRaw(page) {
    var url =
      API_BASE +
      '/reviews?rating=5&sort=newest&from=' +
      REVIEWS_FROM_DATE +
      '&page=' +
      page +
      '&limit=' +
      REVIEWS_PAGE_SIZE;
    return fetchJson(url).then(function (json) {
      if (!json || !json.success || !json.data) {
        throw new Error('SA invalid response');
      }
      var flat = {};
      Object.keys(json.data).forEach(function (key) {
        if (key !== 'total_count') flat[key] = json.data[key];
      });
      return flat;
    });
  }

  // Fetched up front, before any interaction — bigger than the reactive
  // per-scroll refill batch so a fast continuous drag has a large enough
  // cushion to never outrun the network before the lookahead system kicks in.
  var INITIAL_REVIEW_PAGES = 4;

  function fetchInitialReviewPages() {
    var pageNumbers = [];
    for (var i = 0; i < INITIAL_REVIEW_PAGES; i++) pageNumbers.push(i);

    return Promise.all(pageNumbers.map(fetchReviewsPageRaw)).then(function (pages) {
      var merged = {};
      var lastPageCount = 0;
      pages.forEach(function (page) {
        lastPageCount = Object.keys(page).length;
        Object.keys(page).forEach(function (k) {
          merged[k] = page[k];
        });
      });
      return {
        reviews: merged,
        hasMore: lastPageCount === REVIEWS_PAGE_SIZE,
        nextPage: INITIAL_REVIEW_PAGES
      };
    });
  }

  function loadMoreReviewsPage(page) {
    if (reviewPagePromises[page]) return reviewPagePromises[page];
    var p = fetchReviewsPageRaw(page)
      .then(function (flat) {
        return normalizeReviews(flat);
      })
      .catch(function (err) {
        delete reviewPagePromises[page];
        throw err;
      });
    reviewPagePromises[page] = p;
    return p;
  }

  function getReviews() {
    var cached = readCache(CACHE_REV_KEY);
    var network = revPromise;
    if (!network) {
      network = fetchInitialReviewPages()
        .then(function (payload) {
          writeCache(CACHE_REV_KEY, payload);
          return payload;
        })
        .catch(function (err) {
          revPromise = null;
          if (cached && cached.data) return cached.data;
          throw err;
        });
      revPromise = network;
    }

    if (cached && cached.data) {
      network.catch(function () {});
      return Promise.resolve({
        data: cached.data,
        fromCache: true,
        fresh: isFresh(cached),
        refresh: network
      });
    }

    return network.then(function (data) {
      return { data: data, fromCache: false, fresh: true, refresh: Promise.resolve(data) };
    });
  }

  function formatNumber(n) {
    var num = Number(n) || 0;
    try {
      return num.toLocaleString();
    } catch (e) {
      return String(num);
    }
  }

  function recommendPercent(agg) {
    var rec = (agg && agg.other_questions && agg.other_questions.Recommend) || {};
    var total = Number(rec.total_reviews) || Number(agg.total_reviews) || 0;
    if (!total) return 0;
    var positive = (Number(rec['4_star']) || 0) + (Number(rec['5_star']) || 0);
    return Math.round((positive / total) * 100);
  }

  function decodeHtml(str) {
    if (!str) return '';
    var txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  function formatReviewDate(dateStr) {
    if (!dateStr) return '';
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    try {
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return d.toDateString();
    }
  }

  function normalizeReviews(data) {
    if (!data || typeof data !== 'object') return [];
    var list = [];
    Object.keys(data).forEach(function (key) {
      if (key === 'total_count') return;
      var item = data[key];
      if (item && typeof item === 'object' && item.review_id != null) {
        list.push(item);
      }
    });
    list.sort(function (a, b) {
      return new Date(b.review_date) - new Date(a.review_date);
    });
    return list;
  }

  var STAR_PATH =
    'M8.63379 5.4917L8.73828 5.71143L8.97949 5.73682L12.7852 6.13721L9.8623 8.81885L9.67969 8.98584L9.73145 9.22803L10.5312 12.9917L7.10742 11.0591L6.89453 10.939L6.68066 11.0591L3.25684 12.9907L4.05762 9.22803L4.1084 8.98584L3.92578 8.81885L1.00195 6.13721L4.80957 5.73682L5.05078 5.71143L5.15527 5.4917L6.89355 1.84717L8.63379 5.4917Z';

  /** Square star path (viewBox 0 0 24 24) — avoids edge clipping / uneven 5th star */
  var SA_STAR_PATH =
    'M12 2.5l2.747 6.482 7.003.656-5.3 4.62 1.553 6.842L12 17.65l-5.997 3.45 1.553-6.842-5.3-4.62 7.003-.656L12 2.5z';
  // Path leftmost x in 24x24 viewBox — used to optically align with text
  var SA_STAR_LEFT_PAD = 2.256 / 24;

  function starSvg(size, fill) {
    var s = size || 14;
    return (
      '<svg width="' +
      s +
      '" height="' +
      s +
      '" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" style="display:block;width:' +
      s +
      'px;height:' +
      s +
      'px">' +
      '<path d="' +
      SA_STAR_PATH +
      '" fill="' +
      (fill || STAR_COLOR) +
      '"/>' +
      '</svg>'
    );
  }

  function starSlot(size, fill, clipPct) {
    var s = size || 14;
    var style =
      'display:inline-block;width:' +
      s +
      'px;height:' +
      s +
      'px;min-width:' +
      s +
      'px;min-height:' +
      s +
      'px;line-height:0;vertical-align:middle;position:relative;flex-shrink:0;';
    if (clipPct == null) {
      return '<span class="sa-star sa-star--full" style="' + style + '">' + starSvg(s, fill) + '</span>';
    }
    // Identical stacked stars; clip fill with inset so shape matches full stars
    return (
      '<span class="sa-star sa-star--partial" style="' +
      style +
      'overflow:hidden;">' +
      '<span style="position:absolute;left:0;top:0;width:100%;height:100%;line-height:0">' +
      starSvg(s, '#D1D5DB') +
      '</span>' +
      '<span style="position:absolute;left:0;top:0;width:100%;height:100%;line-height:0;clip-path:inset(0 ' +
      (100 - clipPct) +
      '% 0 0)">' +
      starSvg(s, STAR_COLOR) +
      '</span></span>'
    );
  }

  function starsHtml(rating, size, opts) {
    var r = Number(rating) || 0;
    var full = Math.floor(r);
    var frac = r - full;
    // Use a true half-star (50%) for any fraction — avoids a 90% "clipped" 5th star for 4.9
    var hasHalf = frac >= 0.25;
    if (frac >= 0.95) {
      full = Math.min(5, full + 1);
      hasHalf = false;
    }
    var s = size || 14;
    var shiftLeft = !(opts && opts.centered);
    var html =
      '<span class="sa-stars" aria-hidden="true"' +
      (shiftLeft
        ? ' style="margin-left:-' + (s * SA_STAR_LEFT_PAD).toFixed(2) + 'px"'
        : '') +
      '>';
    for (var i = 1; i <= 5; i++) {
      if (i <= full) {
        html += starSlot(s, STAR_COLOR);
      } else if (i === full + 1 && hasHalf) {
        html += starSlot(s, STAR_COLOR, 50);
      } else {
        html += starSlot(s, '#D1D5DB');
      }
    }
    html += '</span>';
    return html;
  }

  function saLinkAttrs() {
    return 'href="' + SA_REVIEWS_URL + '" target="_blank" rel="nofollow noopener noreferrer"';
  }

  /* ---------- Trust bar ---------- */

  function renderTrustBar(root, agg) {
    if (!root || !agg) return;
    var avg = Number(agg.average_rating) || 0;
    var total = Number(agg.total_reviews) || 0;
    var pct = recommendPercent(agg);
    var excellent = avg >= 4.5;

    var ratingEl = root.querySelector('[data-sa-trust-rating]');
    var starsEl = root.querySelector('[data-sa-trust-stars]');
    var countEl = root.querySelector('[data-sa-trust-count]');
    var recommendEl = root.querySelector('[data-sa-trust-recommend]');
    var leftEl = root.querySelector('[data-sa-trust-left]');

    if (ratingEl) {
      ratingEl.textContent = avg.toFixed(1);
      ratingEl.style.cssText =
        'font-size:16px;font-weight:700;line-height:1;display:inline-block;';
      ratingEl.classList.remove('font-normal');
      ratingEl.classList.add('font-bold');
    }
    if (starsEl) {
      starsEl.innerHTML = starsHtml(avg, 20);
    }
    if (countEl) {
      countEl.textContent =
        '(' + formatNumber(total) + ' ' + t('reviews', 'reviews') + ')';
      countEl.style.cssText =
        'font-size:16px;font-weight:700;line-height:1;display:inline-block;white-space:nowrap;';
      countEl.classList.add('font-bold');
    }

    var linkEl = root.querySelector('[data-sa-trust-link]');
    if (linkEl) {
      linkEl.setAttribute('href', SA_REVIEWS_URL);
      linkEl.setAttribute('target', '_blank');
      linkEl.setAttribute('rel', 'nofollow noopener noreferrer');
    }
    if (recommendEl) {
      recommendEl.innerHTML =
        '<span style="font-weight:700">' +
        pct +
        '%</span> ' +
        t('recommended', 'Recommended');
    }
    if (leftEl) {
      var label = excellent
        ? t('rated_excellent', 'Rated "Excellent" by Shopper Approved®')
        : t('rated_by', 'Rated by Shopper Approved®');
      leftEl.innerHTML = String(label).replace(
        /Excellent/gi,
        '<strong style="font-weight:700">Excellent</strong>'
      );
    }

    root.classList.add('is-ready');
    root.classList.remove('is-failed');
    root.removeAttribute('hidden');
  }

  /* ---------- Footer / badge SVG ---------- */

  function centerTspan(tspan, centerX) {
    if (!tspan) return;
    try {
      var bbox = tspan.getBBox();
      if (bbox && bbox.width) {
        tspan.setAttribute('x', String(centerX - bbox.width / 2));
      }
    } catch (e) {
      /* not rendered yet */
    }
  }

  function setBadgeValueText(textEl, tspan, value, centerX) {
    if (!tspan || value == null) return;
    if (textEl) {
      textEl.setAttribute('font-weight', '700');
      textEl.setAttribute('text-anchor', 'middle');
      textEl.setAttribute('x', String(centerX));
    }
    tspan.textContent = value;
    tspan.setAttribute('text-anchor', 'middle');
    tspan.setAttribute('x', String(centerX));
    tspan.setAttribute('font-weight', '700');
    // Recenter after paint — text-anchor middle can fail with mixed attrs from Figma export
    requestAnimationFrame(function () {
      tspan.removeAttribute('text-anchor');
      if (textEl) textEl.removeAttribute('text-anchor');
      centerTspan(tspan, centerX);
    });
  }

  function setBadgeLabelText(textEl, line1, line2, centerX) {
    if (!textEl) return;
    var tspans = textEl.querySelectorAll('tspan');
    if (tspans[0] && line1 != null) tspans[0].textContent = line1;
    if (tspans[1] && line2 != null) tspans[1].textContent = line2;
    textEl.setAttribute('x', String(centerX));
    requestAnimationFrame(function () {
      if (tspans[0]) centerTspan(tspans[0], centerX);
      if (tspans[1]) centerTspan(tspans[1], centerX);
    });
  }

  function fillBadgeSvg(svg, agg) {
    if (!svg || !agg) return;
    var fiveStar = Number(agg['5_star']) || 0;
    var avg = Number(agg.average_rating) || 0;
    var total = Number(agg.total_reviews) || 0;

    var values = [
      formatNumber(fiveStar) + '+',
      avg % 1 === 0 ? String(avg) : avg.toFixed(1),
      formatNumber(total)
    ];

    var labels = [
      [t('five_star', '5-Star'), t('ratings', 'Ratings')],
      [t('overall', 'Overall'), t('rating', 'Rating')],
      [t('total', 'Total'), t('reviews_label', 'Reviews')]
    ];

    var groups = svg.querySelectorAll('g[data-sa-badge-item]');
    if (!groups.length) {
      var tspans = svg.querySelectorAll('.badge-value tspan');
      tspans.forEach(function (valueTspan, idx) {
        if (values[idx] == null) return;
        var textEl = valueTspan.closest('text');
        setBadgeValueText(textEl, valueTspan, values[idx], 90);
      });
      var labelTexts = svg.querySelectorAll('.badge-content text');
      labelTexts.forEach(function (labelText, idx) {
        if (!labels[idx]) return;
        setBadgeLabelText(labelText, labels[idx][0], labels[idx][1], 90);
      });
      return;
    }

    groups.forEach(function (g, idx) {
      var valueTspan = g.querySelector('.badge-value tspan');
      var textEl = g.querySelector('.badge-value text');
      if (valueTspan && values[idx] != null) {
        setBadgeValueText(textEl, valueTspan, values[idx], 90);
      }
      var labelText = g.querySelector('.badge-content text');
      if (labelText && labels[idx]) {
        setBadgeLabelText(labelText, labels[idx][0], labels[idx][1], 90);
      }
    });
  }

  var badgeSvgPromise = null;
  var badgeIdSeq = 0;

  function loadBadgeSvg(src) {
    if (!src) return Promise.reject(new Error('missing badge src'));
    if (badgeSvgPromise) return badgeSvgPromise;
    badgeSvgPromise = fetch(src, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('badge svg fetch failed');
        return res.text();
      })
      .catch(function (err) {
        badgeSvgPromise = null;
        throw err;
      });
    return badgeSvgPromise;
  }

  /** Make clipPath / mask / filter ids unique per badge instance (page can mount multiple). */
  function uniquifySvgIds(svg) {
    if (!svg || svg.getAttribute('data-sa-ids-unique') === 'true') return;
    badgeIdSeq += 1;
    var suffix = '-sa' + badgeIdSeq;
    var idMap = {};

    svg.querySelectorAll('[id]').forEach(function (el) {
      var oldId = el.getAttribute('id');
      if (!oldId) return;
      var newId = oldId + suffix;
      idMap[oldId] = newId;
      el.setAttribute('id', newId);
    });

    function rewriteUrlRefs(value) {
      if (!value || value.indexOf('url(#') === -1) return value;
      return value.replace(/url\(#([^)]+)\)/g, function (match, id) {
        return idMap[id] ? 'url(#' + idMap[id] + ')' : match;
      });
    }

    function rewriteHashRef(value) {
      if (!value || value.charAt(0) !== '#') return value;
      var id = value.slice(1);
      return idMap[id] ? '#' + idMap[id] : value;
    }

    var attrs = ['clip-path', 'fill', 'stroke', 'filter', 'mask', 'marker-start', 'marker-mid', 'marker-end'];
    svg.querySelectorAll('*').forEach(function (el) {
      attrs.forEach(function (attr) {
        var val = el.getAttribute(attr);
        if (!val) return;
        var next = rewriteUrlRefs(val);
        if (next !== val) el.setAttribute(attr, next);
      });

      ['href', 'xlink:href'].forEach(function (attr) {
        var val = el.getAttribute(attr);
        if (!val) return;
        var next = rewriteHashRef(val);
        if (next !== val) el.setAttribute(attr, next);
      });

      var style = el.getAttribute('style');
      if (style && style.indexOf('url(#') !== -1) {
        el.setAttribute('style', rewriteUrlRefs(style));
      }
    });

    svg.setAttribute('data-sa-ids-unique', 'true');
  }

  function renderBadgeMount(root, agg) {
    if (!root || !agg) return;
    var slot = root.querySelector('[data-sa-badge-slot]') || root;
    var existing = root.querySelector('svg[data-sa-badge-svg]');
    if (existing) {
      uniquifySvgIds(existing);
      fillBadgeSvg(existing, agg);
      root.classList.add('is-ready');
      root.classList.remove('is-failed');
      requestAnimationFrame(function () {
        fillBadgeSvg(existing, agg);
      });
      return;
    }

    var src = root.getAttribute('data-sa-badge-src');
    loadBadgeSvg(src)
      .then(function (markup) {
        slot.innerHTML = markup;
        var svg = root.querySelector('svg[data-sa-badge-svg]') || root.querySelector('svg');
        if (svg && !svg.getAttribute('data-sa-badge-svg')) {
          svg.setAttribute('data-sa-badge-svg', '');
        }
        uniquifySvgIds(svg);
        fillBadgeSvg(svg, agg);
        root.classList.add('is-ready');
        root.classList.remove('is-failed');
        // Second pass after layout so getBBox centering is accurate
        requestAnimationFrame(function () {
          fillBadgeSvg(svg, agg);
        });
      })
      .catch(function () {
        markFailed(root);
      });
  }

  /* ---------- Carousel ---------- */

  function truncateText(text, maxLen) {
    var s = (text || '').trim();
    if (s.length <= maxLen) return { text: s, truncated: false };
    var cut = s.slice(0, maxLen);
    var lastSpace = cut.lastIndexOf(' ');
    if (lastSpace > maxLen * 0.5) cut = cut.slice(0, lastSpace);
    cut = cut.replace(/[\s.…]+$/g, '').trim();
    return { text: cut + '...', truncated: true, full: s };
  }

  function summaryCardHtml(agg) {
    var avg = Number(agg.average_rating) || 0;
    var total = Number(agg.total_reviews) || 0;
    return (
      '<div class="sa-review-card sa-review-card--summary">' +
      '<div class="sa-summary-rating">' +
      '<span class="sa-summary-score">' +
      avg.toFixed(1) +
      '</span>' +
      '<span class="sa-summary-slash">/</span>' +
      '<span class="sa-summary-out-of">5</span></div>' +
      '<div class="sa-summary-stars" style="margin-top:10px;line-height:0">' +
      starsHtml(avg, 28, { centered: true }) +
      '</div>' +
      '<a ' +
      saLinkAttrs() +
      ' class="sa-summary-verified" style="margin-top:12px;font-size:0.8125rem;font-weight:700;color:#1f2937;text-decoration:underline;text-underline-offset:2px">' +
      formatNumber(total) +
      ' ' +
      t('verified_reviews', 'Verified Reviews') +
      '</a>' +
      '<a ' +
      saLinkAttrs() +
      ' class="sa-summary-view-all" style="margin-top:8px;font-size:0.9375rem;font-weight:700;color:#1f2937">' +
      t('view_all_reviews', 'View All Reviews') +
      '</a>' +
      '</div>'
    );
  }

  function reviewSlideHtml(review) {
    var comments = decodeHtml(review.comments || '').trim();
    // ~4 lines in a ~188px-wide card at text-sm; keep "..." above More
    var truncated = truncateText(comments, 110);
    var moreBtn = truncated.truncated
      ? '<button type="button" class="sa-review-more text-sm text-gray-700 underline underline-offset-2 self-end shrink-0" data-sa-more>' +
        t('more', 'More') +
        '</button>'
      : '';

    return (
      '<div class="splide__slide">' +
      '<article class="sa-review-card sa-review-card--slide"' +
      ' data-sa-full="' +
      encodeURIComponent(comments) +
      '"' +
      ' data-sa-name="' +
      encodeURIComponent(review.display_name || '') +
      '"' +
      ' data-sa-date="' +
      encodeURIComponent(formatReviewDate(review.review_date)) +
      '"' +
      ' data-sa-rating="' +
      encodeURIComponent(String(review.overall || 5)) +
      '">' +
      '<div class="mb-2 leading-none shrink-0">' +
      starsHtml(review.overall || 5, 22) +
      '</div>' +
      '<div class="sa-review-name shrink-0">' +
      escapeHtml(review.display_name || '') +
      '</div>' +
      '<div class="text-sm text-gray-500 mt-0.5 shrink-0">' +
      escapeHtml(formatReviewDate(review.review_date)) +
      '</div>' +
      '<p class="sa-review-body text-sm text-gray-800 mt-3">' +
      escapeHtml(truncated.text) +
      '</p>' +
      moreBtn +
      '</article></div>'
    );
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function closeReviewPopup(root) {
    var popup = root.querySelector('[data-sa-popup]');
    if (!popup || popup.hasAttribute('hidden')) return;

    var popupCard = popup.querySelector('.sa-review-popup__card');
    var finish = function () {
      if (popup.hasAttribute('hidden')) return;
      popup.setAttribute('hidden', '');
      if (popupCard) popupCard.classList.remove('is-visible');
      if (root._saPopupCard) {
        root._saPopupCard.classList.remove('sa-review-card--expanded');
      }
      root._saPopupCard = null;
      if (root._saPopupResize) {
        window.removeEventListener('resize', root._saPopupResize);
        root._saPopupResize = null;
      }
      if (root._saPopupScrollClose) {
        window.removeEventListener('scroll', root._saPopupScrollClose, true);
        root._saPopupScrollClose = null;
      }
      if (root._saSplide && root._saPopupMoveHandler) {
        try {
          root._saSplide.off('move', root._saPopupMoveHandler);
        } catch (e) {}
        root._saPopupMoveHandler = null;
      }
      if (root._saSplide && root._saSplide.Components && root._saSplide.Components.Autoplay) {
        try {
          root._saSplide.Components.Autoplay.play();
        } catch (e) {}
      }
    };

    if (popupCard && popupCard.classList.contains('is-visible')) {
      popupCard.classList.remove('is-visible');
      var ended = false;
      var onEnd = function (event) {
        if (ended) return;
        if (event && event.target !== popupCard) return;
        if (event && event.propertyName && event.propertyName !== 'transform') return;
        ended = true;
        popupCard.removeEventListener('transitionend', onEnd);
        finish();
      };
      popupCard.addEventListener('transitionend', onEnd);
      setTimeout(function () {
        onEnd(null);
      }, 320);
    } else {
      finish();
    }
  }

  function positionReviewPopup(popupCard, anchorCard) {
    if (!popupCard || !anchorCard) return;
    var rect = anchorCard.getBoundingClientRect();
    var pad = 8;
    // Slightly larger than the source card — “magnified” over it
    var width = Math.min(rect.width + 24, window.innerWidth - pad * 2);
    var height = Math.min(rect.height + 32, window.innerHeight - pad * 2);

    popupCard.style.width = Math.round(width) + 'px';
    popupCard.style.height = Math.round(height) + 'px';
    popupCard.style.maxHeight = Math.round(height) + 'px';

    var left = rect.left + (rect.width - width) / 2;
    var top = rect.top + (rect.height - height) / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - height - pad));

    popupCard.style.left = Math.round(left) + 'px';
    popupCard.style.top = Math.round(top) + 'px';
    popupCard.style.right = 'auto';
    popupCard.style.bottom = 'auto';
  }

  function openReviewPopup(root, card) {
    var popup = root.querySelector('[data-sa-popup]');
    if (!popup || !card) return;

    var full = decodeURIComponent(card.getAttribute('data-sa-full') || '');
    var name = decodeURIComponent(card.getAttribute('data-sa-name') || '');
    var date = decodeURIComponent(card.getAttribute('data-sa-date') || '');
    var rating = decodeURIComponent(card.getAttribute('data-sa-rating') || '5');

    var popupCard = popup.querySelector('.sa-review-popup__card');
    var starsEl = popup.querySelector('[data-sa-popup-stars]');
    var nameEl = popup.querySelector('[data-sa-popup-name]');
    var dateEl = popup.querySelector('[data-sa-popup-date]');
    var bodyEl = popup.querySelector('[data-sa-popup-body]');

    if (root._saPopupCard && root._saPopupCard !== card) {
      root._saPopupCard.classList.remove('sa-review-card--expanded');
    }
    if (starsEl) starsEl.innerHTML = starsHtml(rating, 22);
    if (nameEl) nameEl.textContent = name;
    if (dateEl) dateEl.textContent = date;
    if (bodyEl) {
      bodyEl.textContent = full;
      bodyEl.scrollTop = 0;
    }

    root._saPopupCard = card;
    card.classList.add('sa-review-card--expanded');
    if (popupCard) popupCard.classList.remove('is-visible');

    popup.removeAttribute('hidden');

    if (popupCard) {
      positionReviewPopup(popupCard, card);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          popupCard.classList.add('is-visible');
        });
      });
    }

    if (root._saPopupResize) window.removeEventListener('resize', root._saPopupResize);
    if (root._saPopupScrollClose) window.removeEventListener('scroll', root._saPopupScrollClose, true);

    root._saPopupResize = function () {
      if (!root._saPopupCard || !popupCard) return;
      positionReviewPopup(popupCard, root._saPopupCard);
    };
    window.addEventListener('resize', root._saPopupResize);

    // Close as soon as the user starts scrolling the page, rather than
    // tracking the popup along with it. Scroll events don't bubble but do
    // fire during the capture phase, so ignore ones from inside the popup
    // itself (e.g. scrolling a long review body) or this would close on
    // that too.
    root._saPopupScrollClose = function (event) {
      if (event && event.target && popup.contains(event.target)) return;
      closeReviewPopup(root);
    };
    window.addEventListener('scroll', root._saPopupScrollClose, true);

    if (root._saSplide) {
      if (root._saPopupMoveHandler) {
        try {
          root._saSplide.off('move', root._saPopupMoveHandler);
        } catch (e) {}
      }
      root._saPopupMoveHandler = function () {
        closeReviewPopup(root);
      };
      try {
        root._saSplide.on('move', root._saPopupMoveHandler);
      } catch (e) {}
      if (root._saSplide.Components && root._saSplide.Components.Autoplay) {
        try {
          root._saSplide.Components.Autoplay.pause();
        } catch (e) {}
      }
    }
  }

  function bindReviewPopup(root) {
    if (root.getAttribute('data-sa-popup-bound') === 'true') return;
    root.setAttribute('data-sa-popup-bound', 'true');

    root.addEventListener('click', function (event) {
      var moreBtn = event.target.closest('[data-sa-more]');
      if (moreBtn && root.contains(moreBtn)) {
        event.preventDefault();
        var card = moreBtn.closest('.sa-review-card');
        openReviewPopup(root, card);
        return;
      }
      var closer = event.target.closest('[data-sa-popup-close]');
      if (closer && root.contains(closer)) {
        event.preventDefault();
        closeReviewPopup(root);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var popup = root.querySelector('[data-sa-popup]');
      if (popup && !popup.hasAttribute('hidden')) closeReviewPopup(root);
    });
  }

  function whenSplideReady(cb) {
    if (typeof Splide !== 'undefined') {
      cb();
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (typeof Splide !== 'undefined') {
        clearInterval(timer);
        cb();
      } else if (tries >= 50) {
        clearInterval(timer);
      }
    }, 50);
  }

  // Refill once this many unrendered slides remain ahead of the current position.
  var LOAD_MORE_LOOKAHEAD = REVIEWS_PAGE_SIZE;
  // Pages fetched per refill — more than 1 so the buffer doesn't run dry mid-request.
  var PREFETCH_BATCH_PAGES = 2;

  // splide.add() repositions the carousel, which Splide's own docs say not
  // to do while it's mid-move. A continuous drag can keep the carousel
  // MOVING right through when a background fetch resolves, so wait for it
  // to go IDLE before appending instead of corrupting the drag mid-gesture.
  function appendReviewSlides(splide, items) {
    if (!items.length) return;
    var slidesHtml = items.map(reviewSlideHtml);
    if (splide.state.is(Splide.STATES.IDLE)) {
      splide.add(slidesHtml);
      return;
    }
    var onIdle = function () {
      if (!splide.state.is(Splide.STATES.IDLE)) return;
      splide.off('moved', onIdle);
      splide.add(slidesHtml);
    };
    splide.on('moved', onIdle);
  }

  function maybeLoadMoreReviews(root) {
    var splide = root._saSplide;
    var state = root._saPageState;
    if (!splide || !state || state.loadingMore || !state.hasMore) return;
    var remaining = splide.length - 1 - splide.index;
    if (remaining > LOAD_MORE_LOOKAHEAD) return;

    state.loadingMore = true;
    var startPage = state.nextPage;
    var pageNumbers = [];
    for (var i = 0; i < PREFETCH_BATCH_PAGES; i++) pageNumbers.push(startPage + i);

    Promise.all(
      pageNumbers.map(function (page) {
        // Resolve to null on failure so one bad page doesn't discard the rest of the batch.
        return loadMoreReviewsPage(page).catch(function () {
          return null;
        })
      })
    ).then(function (results) {
      state.loadingMore = false;

      var appended = [];
      var cursor = startPage;
      for (var j = 0; j < results.length; j++) {
        var items = results[j];
        if (items == null) break; // fetch failed — retry this page next time
        appended = appended.concat(items);
        cursor = pageNumbers[j] + 1;
        if (items.length < REVIEWS_PAGE_SIZE) {
          state.hasMore = false;
          break;
        }
        if (j === results.length - 1) state.hasMore = true;
      }
      state.nextPage = cursor;

      appendReviewSlides(splide, appended);
      if (root._saCheckEnds) root._saCheckEnds();
    });
  }

  function initSaReviewsSplide(root, listEl, pageState) {
    if (typeof Splide === 'undefined' || !listEl) return null;
    var shopLocale =
      (document.documentElement && document.documentElement.lang) || 'en';
    var isRTL = shopLocale.indexOf('ar') === 0;

    // perPage (not fixedWidth): each page shows a whole number of cards, no partial peek.
    var splide = new Splide(listEl, {
      type: 'slide',
      rewind: true,
      autoplay: true,
      interval: 4000,
      pauseOnHover: true,
      pauseOnFocus: true,
      resetProgress: false,
      perPage: 5,
      perMove: 1,
      gap: '0.75rem',
      arrows: false,
      pagination: false,
      drag: true,
      speed: 600,
      direction: isRTL ? 'rtl' : 'ltr',
      breakpoints: {
        640: {
          perPage: 1,
          gap: '0.625rem'
        },
        1024: {
          perPage: 2,
          gap: '0.75rem'
        },
        1280: {
          perPage: 3,
          gap: '1rem'
        }
      }
    });

    root._saSplide = splide;
    root._saPageState = pageState || { nextPage: 2, hasMore: false, loadingMore: false };

    var prevBtn = root.querySelector('[data-sa-prev]');
    var nextBtn = root.querySelector('[data-sa-next]');

    function checkEnds() {
      if (!prevBtn || !nextBtn || !splide.Components || !splide.Components.Controller) return;
      var end = splide.Components.Controller.getEnd();
      var state = root._saPageState;
      prevBtn.disabled = splide.index <= 0;
      // Never disable "next" mid-fetch — only once we're confirmed out of data.
      nextBtn.disabled = splide.index >= end && !state.hasMore && !state.loadingMore;
    }
    root._saCheckEnds = checkEnds;

    // Same as the product carousel: disable arrows on 'move' (fires as the
    // transition to the destination page starts), not 'moved' (fires only
    // once it finishes).
    splide.on('mounted updated move', checkEnds);
    // Track direction ourselves rather than trusting the 'moved' callback's
    // (newIndex, prevIndex) args, which aren't reliably populated for a
    // drag-completed move the way they are for an arrow-triggered go() —
    // that gap was silently skipping the refill whenever you dragged.
    var lastIndex = 0;
    splide.on('moved', function () {
      var current = splide.index;
      // Only refill when moving toward the unloaded edge (forward).
      if (current > lastIndex) maybeLoadMoreReviews(root);
      lastIndex = current;
    });
    // Arrow clicks get an immediate refill check (see onclick below) instead
    // of waiting on an event; a fast continuous drag can cover the lookahead
    // buffer faster than a 'moved' round-trip, so give drag the same
    // immediate check right as the gesture starts.
    splide.on('drag', function () {
      maybeLoadMoreReviews(root);
    });
    splide.mount();
    lastIndex = splide.index;
    checkEnds();
    maybeLoadMoreReviews(root);

    if (prevBtn) {
      prevBtn.onclick = function () {
        splide.go('<');
      };
    }
    if (nextBtn) {
      nextBtn.onclick = function () {
        splide.go('>');
        maybeLoadMoreReviews(root);
      };
    }

    return splide;
  }

  function renderCarousel(root, agg, reviews, pageState) {
    if (!root) return;
    var summaryEl = root.querySelector('[data-sa-summary]');
    var track = root.querySelector('[data-sa-splide-list]');
    var splideRoot = root.querySelector('[data-sa-splide]');
    if (!track || !splideRoot) return;

    if (summaryEl) summaryEl.innerHTML = summaryCardHtml(agg || {});

    var html = '';
    (reviews || []).forEach(function (r) {
      html += reviewSlideHtml(r);
    });
    track.innerHTML = html;
    bindReviewPopup(root);

    if (root._saSplide) {
      try {
        root._saSplide.destroy(true);
      } catch (e) {}
      root._saSplide = null;
    }

    // Show as soon as data is in the DOM (do not wait on Splide)
    root.classList.add('is-ready');
    root.classList.remove('is-failed');
    root.removeAttribute('hidden');

    var state = {
      nextPage: (pageState && pageState.nextPage) || 2,
      hasMore: !!(pageState && pageState.hasMore),
      loadingMore: false
    };

    whenSplideReady(function () {
      initSaReviewsSplide(root, splideRoot, state);
    });
  }

  function markFailed(root) {
    if (!root) return;
    root.classList.add('is-failed');
    root.classList.remove('is-ready');
    root.setAttribute('hidden', '');
  }

  /* ---------- Bootstrappers ---------- */

  function activateTrust(root) {
    if (!root || root.getAttribute('data-sa-activated') === 'true') return;
    root.setAttribute('data-sa-activated', 'true');
    getAggregates()
      .then(function (result) {
        renderTrustBar(root, result.data);
        if (result.refresh) {
          result.refresh.then(function (fresh) {
            renderTrustBar(root, fresh);
          }).catch(function () {});
        }
      })
      .catch(function () {
        markFailed(root);
      });
  }

  function activateBadge(root) {
    if (!root || root.getAttribute('data-sa-activated') === 'true') return;
    root.setAttribute('data-sa-activated', 'true');
    getAggregates()
      .then(function (result) {
        renderBadgeMount(root, result.data);
        if (result.refresh) {
          result.refresh.then(function (fresh) {
            renderBadgeMount(root, fresh);
          }).catch(function () {});
        }
      })
      .catch(function () {
        markFailed(root);
      });
  }

  function settle(promise) {
    return Promise.resolve(promise).then(
      function (value) {
        return { status: 'fulfilled', value: value };
      },
      function (reason) {
        return { status: 'rejected', reason: reason };
      }
    );
  }

  function scheduleCarouselRetry(root) {
    var retries = Number(root.getAttribute('data-sa-retries') || 0);
    if (retries >= 2) return;
    root.setAttribute('data-sa-retries', String(retries + 1));
    setTimeout(function () {
      if (root.getAttribute('data-sa-activated') === 'true') return;
      activateCarousel(root);
    }, 3000);
  }

  function activateCarousel(root) {
    if (!root || root.getAttribute('data-sa-activated') === 'true') return;
    if (root.getAttribute('data-sa-loading') === 'true') return;
    root.setAttribute('data-sa-loading', 'true');

    Promise.all([settle(getAggregates()), settle(getReviews())])
      .then(function (results) {
        root.removeAttribute('data-sa-loading');

        var aggResult = results[0].status === 'fulfilled' ? results[0].value : null;
        var revResult = results[1].status === 'fulfilled' ? results[1].value : null;
        var agg = aggResult && aggResult.data ? aggResult.data : null;
        var revPayload = revResult ? revResult.data : null;
        var reviews = revPayload ? normalizeReviews(revPayload.reviews) : [];
        var hasAgg = !!agg;
        var hasReviews = reviews.length > 0;

        // Both aggregates and reviews must succeed before rendering.
        if (!hasAgg || !hasReviews) {
          markFailed(root);
          scheduleCarouselRetry(root);
          return;
        }

        root.setAttribute('data-sa-activated', 'true');
        renderCarousel(root, agg, reviews, {
          hasMore: !!(revPayload && revPayload.hasMore),
          nextPage: (revPayload && revPayload.nextPage) || 2
        });

        var refreshAgg =
          aggResult && (aggResult.refresh || Promise.resolve(aggResult.data));
        var refreshRev =
          revResult && (revResult.refresh || Promise.resolve(revResult.data));

        if (!refreshAgg || !refreshRev) return;

        Promise.all([refreshAgg, refreshRev])
          .then(function (fresh) {
            var nextAgg = fresh[0];
            var nextRevPayload = fresh[1];
            var nextReviews = nextRevPayload ? normalizeReviews(nextRevPayload.reviews) : [];
            if (!nextAgg || !nextReviews.length) return;
            if (nextAgg === agg && nextRevPayload === revPayload) return;
            renderCarousel(root, nextAgg, nextReviews, {
              hasMore: !!(nextRevPayload && nextRevPayload.hasMore),
              nextPage: (nextRevPayload && nextRevPayload.nextPage) || 2
            });
          })
          .catch(function () {});
      })
      .catch(function () {
        root.removeAttribute('data-sa-loading');
        markFailed(root);
        scheduleCarouselRetry(root);
      });
  }

  function observeOrRun(root, activator) {
    if (!root || root.getAttribute('data-sa-bound') === 'true') return;
    root.setAttribute('data-sa-bound', 'true');

    if (!('IntersectionObserver' in window)) {
      activator(root);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          activator(entry.target);
        });
      },
      { rootMargin: LOAD_ROOT_MARGIN, threshold: 0 }
    );
    io.observe(root);
  }

  function runNow(root, activator) {
    if (!root || root.getAttribute('data-sa-bound') === 'true') return;
    root.setAttribute('data-sa-bound', 'true');
    activator(root);
  }

  function bindAll() {
    readI18n();
    // Fetch immediately; UI stays hidden until is-ready (display:none would
    // prevent IntersectionObserver from ever firing).
    document.querySelectorAll('[data-sa-trust-root]').forEach(function (el) {
      runNow(el, activateTrust);
    });
    document.querySelectorAll('[data-sa-badge-root]').forEach(function (el) {
      runNow(el, activateBadge);
    });
    document.querySelectorAll('[data-sa-carousel-root]').forEach(function (el) {
      runNow(el, activateCarousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', bindAll);

  window.ShopperApprovedReviews = {
    getAggregates: getAggregates,
    getReviews: getReviews,
    bindAll: bindAll,
    SA_REVIEWS_URL: SA_REVIEWS_URL
  };
})();
